import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Textarea } from '../components/ui/textarea';
import { User, Package, MapPin, Clock, Shield, Building2, Bell, Loader2, Mail, Pencil, AlertTriangle, Plus, Trash2, Star, MessageSquare, Lock, Send, Eye } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountPage() {
  const { user, logout, fetchUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [tradeApp, setTradeApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({ new_email: '', password: '' });
  const [emailChanging, setEmailChanging] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState({
    order_updates: true,
    ticket_updates: true,
    marketing_emails: false,
    estimate_reminders: true,
    invoice_reminders: true,
    backorder_updates: true
  });
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({ label: '', street: '', city: '', state: '', postcode: '', country: 'Australia', is_default: false });
  const [addressSaving, setAddressSaving] = useState(false);

  // Support tickets state
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [showNewTicketDialog, setShowNewTicketDialog] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', description: '', priority: 'medium' });
  const [ticketCreating, setTicketCreating] = useState(false);

  // Password change state
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [passwordChanging, setPasswordChanging] = useState(false);

  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchData();
    fetchNotificationPrefs();
    fetchAddresses();
    fetchTickets();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, tradeRes] = await Promise.all([
        axios.get(`${API}/orders`, authHeaders),
        axios.get(`${API}/trade-application/status`, authHeaders).catch(() => ({ data: null })),
      ]);
      setOrders(ordersRes.data);
      setTradeApp(tradeRes.data);
    } catch (err) {
      console.error('Error fetching account data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationPrefs = async () => {
    try {
      const res = await axios.get(`${API}/notification-preferences`, authHeaders);
      setNotificationPrefs(res.data);
    } catch (err) {
      console.error('Error fetching notification preferences:', err);
    }
  };

  const updateNotificationPref = async (key, value) => {
    const newPrefs = { ...notificationPrefs, [key]: value };
    setNotificationPrefs(newPrefs);
    setSavingPrefs(true);
    
    try {
      await axios.put(`${API}/notification-preferences`, newPrefs, authHeaders);
      toast.success('Preferences updated');
    } catch (err) {
      // Revert on error
      setNotificationPrefs(notificationPrefs);
      toast.error('Failed to update preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  // ── Address Management ──────────────────────────────────────
  const fetchAddresses = async () => {
    try {
      const res = await axios.get(`${API}/auth/addresses`, authHeaders);
      setAddresses(res.data.addresses || []);
    } catch (err) {
      console.error('Error fetching addresses:', err);
    }
  };

  const openAddAddress = () => {
    setEditingAddress(null);
    setAddressForm({ label: '', street: '', city: '', state: '', postcode: '', country: 'Australia', is_default: false });
    setShowAddressDialog(true);
  };

  const openEditAddress = (addr) => {
    setEditingAddress(addr);
    setAddressForm({
      label: addr.label || '',
      street: addr.street || '',
      city: addr.city || '',
      state: addr.state || '',
      postcode: addr.postcode || '',
      country: addr.country || 'Australia',
      is_default: addr.is_default || false,
    });
    setShowAddressDialog(true);
  };

  const handleSaveAddress = async () => {
    if (!addressForm.street || !addressForm.city || !addressForm.state || !addressForm.postcode) {
      toast.error('Please fill in all required fields');
      return;
    }
    setAddressSaving(true);
    try {
      if (editingAddress) {
        const res = await axios.put(`${API}/auth/addresses/${editingAddress.id}`, addressForm, authHeaders);
        setAddresses(res.data.addresses);
        toast.success('Address updated');
      } else {
        const res = await axios.post(`${API}/auth/addresses`, addressForm, authHeaders);
        setAddresses(res.data.addresses);
        toast.success('Address added');
      }
      setShowAddressDialog(false);
      if (fetchUser) fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save address');
    } finally {
      setAddressSaving(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      const res = await axios.delete(`${API}/auth/addresses/${addressId}`, authHeaders);
      setAddresses(res.data.addresses);
      toast.success('Address deleted');
      if (fetchUser) fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete address');
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      const res = await axios.put(`${API}/auth/addresses/${addressId}/default`, {}, authHeaders);
      setAddresses(res.data.addresses);
      toast.success('Default address updated');
      if (fetchUser) fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set default');
    }
  };

  // ── Support Tickets ──────────────────────────────────────
  const fetchTickets = async () => {
    setTicketsLoading(true);
    try {
      const res = await axios.get(`${API}/my/tickets`, authHeaders);
      setTickets(res.data.tickets || []);
    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setTicketsLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject.trim()) { toast.error('Please enter a subject'); return; }
    setTicketCreating(true);
    try {
      await axios.post(`${API}/my/tickets`, newTicket, authHeaders);
      toast.success('Ticket submitted! We\'ll get back to you soon.');
      setShowNewTicketDialog(false);
      setNewTicket({ subject: '', description: '', priority: 'medium' });
      fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setTicketCreating(false);
    }
  };

  // ── Password Change ─────────────────────────────────────
  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error('Please fill in all fields'); return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error('New password must be at least 8 characters'); return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match'); return;
    }
    setPasswordChanging(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      }, authHeaders);
      toast.success('Password updated successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordChanging(false);
    }
  };


  const resendVerification = async () => {
    setResendingVerification(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, {}, authHeaders);
      toast.success('Verification email sent! Check your inbox.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send verification email');
    } finally {
      setResendingVerification(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!emailForm.new_email || !emailForm.password) {
      toast.error('Please fill in both fields');
      return;
    }
    setEmailChanging(true);
    try {
      const res = await axios.post(`${API}/auth/change-email`, emailForm, authHeaders);
      toast.success(res.data.message);
      setShowEmailDialog(false);
      setEmailForm({ new_email: '', password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request email change');
    } finally {
      setEmailChanging(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-500';
      case 'processing': return 'bg-blue-500/20 text-blue-500';
      case 'shipped': return 'bg-purple-500/20 text-purple-500';
      case 'delivered': return 'bg-[#78BE20]/20 text-[#78BE20]';
      case 'cancelled': return 'bg-red-500/20 text-red-500';
      case 'approved': return 'bg-[#78BE20]/20 text-[#78BE20]';
      case 'rejected': return 'bg-red-500/20 text-red-500';
      default: return 'bg-zinc-500/20 text-zinc-500';
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="account-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black text-foreground uppercase tracking-tight">
              My Account
            </h1>
            <p className="text-muted-foreground mt-1">
              {user?.email}
            </p>
          </div>
          <Button variant="outline" onClick={logout} className="border-zinc-700">
            Logout
          </Button>
        </div>

        {/* Email Verification Banner */}
        {user && user.email_verified === false && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-4 mb-6 flex items-center justify-between" data-testid="email-verify-banner">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
              <div>
                <p className="text-foreground font-medium text-sm">Email address not verified</p>
                <p className="text-muted-foreground text-xs">Please check your inbox for a verification link, or click resend below.</p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10 flex-shrink-0"
              onClick={resendVerification}
              disabled={resendingVerification}
              data-testid="resend-verification-btn"
            >
              {resendingVerification ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Mail className="h-3 w-3 mr-1" />}
              Resend
            </Button>
          </div>
        )}

        {/* Account Tier Badge */}
        <div className="bg-card border border-border rounded-sm p-4 mb-8 flex items-center justify-between">
          <div className="flex items-center">
            <Shield className="h-5 w-5 text-[#78BE20] mr-3" />
            <div>
              <span className="text-sm text-muted-foreground">Account Type:</span>
              <Badge className={`ml-2 ${user?.account_tier === 'retail' ? 'bg-zinc-700' : 'bg-[#78BE20]/20 text-[#78BE20]'}`}>
                {user?.account_tier?.toUpperCase()}
              </Badge>
            </div>
          </div>
          {user?.account_tier === 'retail' && !tradeApp && (
            <Link to="/trade-application">
              <Button size="sm" className="bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="apply-trade-btn">
                Apply for Trade Account
              </Button>
            </Link>
          )}
          {tradeApp?.status === 'pending' && (
            <Badge className="bg-yellow-500/20 text-yellow-500">Trade Application Pending</Badge>
          )}
        </div>

        {/* Trade Application CTA for retail users */}
        {user?.account_tier === 'retail' && !tradeApp && (
          <div className="bg-card border border-[#78BE20]/20 rounded-sm p-5 flex items-center justify-between" data-testid="trade-cta">
            <div>
              <p className="text-foreground font-semibold">Work in IT, WiSP, or Telecommunications?</p>
              <p className="text-muted-foreground text-sm mt-1">Apply for a Trade account to unlock 15-30% off retail pricing across our full product range.</p>
            </div>
            <Link to="/trade-application" className="flex-shrink-0 ml-4">
              <Button className="bg-[#78BE20] text-black hover:bg-[#65a31b]">Apply Now</Button>
            </Link>
          </div>
        )}

        <Tabs defaultValue="orders" className="space-y-6">
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="orders" className="data-[state=active]:bg-[#78BE20] data-[state=active]:text-black">
              <Package className="h-4 w-4 mr-2" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="profile" className="data-[state=active]:bg-[#78BE20] data-[state=active]:text-black">
              <User className="h-4 w-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="addresses" className="data-[state=active]:bg-[#78BE20] data-[state=active]:text-black">
              <MapPin className="h-4 w-4 mr-2" />
              Addresses
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-[#78BE20] data-[state=active]:text-black" data-testid="notifications-tab">
              <Bell className="h-4 w-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="support" className="data-[state=active]:bg-[#78BE20] data-[state=active]:text-black" data-testid="support-tab">
              <MessageSquare className="h-4 w-4 mr-2" />
              Support
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="bg-card border border-border rounded-sm p-4 animate-pulse">
                    <div className="h-5 bg-zinc-800 rounded w-1/4 mb-2" />
                    <div className="h-4 bg-zinc-800 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-muted-foreground">No orders yet</p>
                <Link to="/products">
                  <Button className="mt-4 bg-[#78BE20] text-black hover:bg-[#65a31b]">
                    Start Shopping
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map(order => (
                  <div key={order.id} className="bg-card border border-border rounded-sm p-6" data-testid={`order-${order.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Order #{order.order_number || order.id.slice(0, 8).toUpperCase()}
                        </p>
                        <p className="text-xs text-zinc-500 flex items-center mt-1">
                          <Clock className="h-3 w-3 mr-1" />
                          {new Date(order.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusColor(order.status)}>
                          {order.status}
                        </Badge>
                        <Badge className={getStatusColor(order.payment_status)}>
                          {order.payment_status}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="space-y-2 border-t border-border pt-4">
                      {order.items?.slice(0, 3).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-muted-foreground">
                            {item.product_name} x {item.quantity}
                          </span>
                          <span className="font-mono">${item.total_price.toFixed(2)}</span>
                        </div>
                      ))}
                      {order.items?.length > 3 && (
                        <p className="text-sm text-muted-foreground">
                          +{order.items.length - 3} more items
                        </p>
                      )}
                    </div>

                    <div className="border-t border-border mt-4 pt-4 flex justify-between items-center">
                      <span className="font-bold text-foreground">Total</span>
                      <span className="font-bold text-[#78BE20] font-mono">${order.total.toFixed(2)}</span>
                    </div>

                    {order.shipping?.tracking_number && (
                      <div className="mt-4 p-3 bg-[#78BE20]/10 rounded-sm">
                        <p className="text-sm text-[#78BE20] flex items-center gap-2">
                          <strong>Tracking:</strong>
                          <a
                            href={
                              (order.shipping.carrier || '').toLowerCase().includes('australia post')
                                ? `https://auspost.com.au/mypost/track/#/details/${order.shipping.tracking_number}`
                                : (order.shipping.carrier || '').toLowerCase().includes('startrack')
                                ? `https://startrack.com.au/track/#/details/${order.shipping.tracking_number}`
                                : (order.shipping.carrier || '').toLowerCase().includes('tnt')
                                ? `https://www.tnt.com/express/en_au/site/tracking.html?searchType=con&cons=${order.shipping.tracking_number}`
                                : `https://auspost.com.au/mypost/track/#/details/${order.shipping.tracking_number}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-white transition-colors font-mono"
                            data-testid={`tracking-link-${order.id}`}
                          >
                            {order.shipping.tracking_number}
                          </a>
                          {order.shipping.carrier && (
                            <span className="text-xs text-muted-foreground">via {order.shipping.carrier}</span>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="bg-card border border-border rounded-sm p-6">
              <h2 className="text-lg font-bold text-foreground mb-4">Profile Information</h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Name</p>
                  <p className="text-foreground font-medium">{user?.first_name} {user?.last_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <div className="flex items-center gap-2">
                    <p className="text-foreground font-medium">{user?.email}</p>
                    {user?.email_verified === false && (
                      <Badge className="bg-amber-500/20 text-amber-500 text-[10px]">Unverified</Badge>
                    )}
                    {user?.email_verified === true && (
                      <Badge className="bg-green-500/20 text-green-400 text-[10px]">Verified</Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowEmailDialog(true)}
                      data-testid="edit-email-btn"
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <div>
                  <p className="text-muted-foreground">Phone</p>
                  <p className="text-foreground font-medium">{user?.phone || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Account Type</p>
                  <p className="text-foreground font-medium capitalize">{user?.account_tier}</p>
                </div>
                {user?.company_name && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground flex items-center gap-1">
                      <Building2 className="h-3 w-3" /> Company
                    </p>
                    <p className="text-foreground font-medium">{user.company_name}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Password Change */}
            <div className="bg-card border border-border rounded-sm p-6 mt-4">
              <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                <Lock className="h-5 w-5" /> Change Password
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-muted-foreground text-sm">Current Password</Label>
                  <Input
                    type="password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm(f => ({ ...f, current_password: e.target.value }))}
                    placeholder="Current password"
                    className="mt-1 bg-zinc-800 border-zinc-700"
                    data-testid="current-password-input"
                  />
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm">New Password</Label>
                  <Input
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm(f => ({ ...f, new_password: e.target.value }))}
                    placeholder="Min 8 characters"
                    className="mt-1 bg-zinc-800 border-zinc-700"
                    data-testid="new-password-input"
                  />
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm">Confirm New Password</Label>
                  <Input
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm(f => ({ ...f, confirm_password: e.target.value }))}
                    placeholder="Confirm new password"
                    className="mt-1 bg-zinc-800 border-zinc-700"
                    data-testid="confirm-new-password-input"
                  />
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <Button
                  onClick={handleChangePassword}
                  disabled={passwordChanging}
                  className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
                  data-testid="change-password-btn"
                >
                  {passwordChanging ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Update Password
                </Button>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="addresses">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-foreground">Saved Addresses</h2>
              <Button
                onClick={openAddAddress}
                className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
                data-testid="add-address-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Address
              </Button>
            </div>
            {addresses.length === 0 ? (
              <div className="text-center py-12">
                <MapPin className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-muted-foreground">No saved addresses</p>
                <p className="text-muted-foreground text-sm mt-1">Add an address to speed up checkout</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {addresses.map((addr) => (
                  <div key={addr.id} className="bg-card border border-border rounded-sm p-4 relative" data-testid={`address-card-${addr.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-foreground">{addr.label || 'Address'}</span>
                      <div className="flex items-center gap-1">
                        {addr.is_default && (
                          <Badge className="bg-[#78BE20]/20 text-[#78BE20]">Default</Badge>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {addr.street}<br />
                      {addr.city}, {addr.state} {addr.postcode}<br />
                      {addr.country}
                    </p>
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-foreground h-8 text-xs"
                        onClick={() => openEditAddress(addr)}
                        data-testid={`edit-address-${addr.id}`}
                      >
                        <Pencil className="h-3 w-3 mr-1" /> Edit
                      </Button>
                      {!addr.is_default && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-[#78BE20] h-8 text-xs"
                          onClick={() => handleSetDefault(addr.id)}
                          data-testid={`set-default-${addr.id}`}
                        >
                          <Star className="h-3 w-3 mr-1" /> Set Default
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-red-400 h-8 text-xs ml-auto"
                        onClick={() => handleDeleteAddress(addr.id)}
                        data-testid={`delete-address-${addr.id}`}
                      >
                        <Trash2 className="h-3 w-3 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" data-testid="notifications-content">
            <div className="bg-card border border-border rounded-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-bold text-foreground">Email Notifications</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Choose which emails you'd like to receive
                  </p>
                </div>
                {savingPrefs && (
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </div>
                )}
              </div>

              <div className="space-y-6">
                {/* Order Updates */}
                <div className="flex items-center justify-between py-3 border-b border-border">
                  <div className="space-y-0.5">
                    <Label htmlFor="order_updates" className="text-base font-medium text-foreground">
                      Order Updates
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Get notified when your order is shipped or delivered
                    </p>
                  </div>
                  <Switch
                    id="order_updates"
                    checked={notificationPrefs.order_updates}
                    onCheckedChange={(val) => updateNotificationPref('order_updates', val)}
                    data-testid="toggle-order-updates"
                  />
                </div>

                {/* Ticket Updates */}
                <div className="flex items-center justify-between py-3 border-b border-border">
                  <div className="space-y-0.5">
                    <Label htmlFor="ticket_updates" className="text-base font-medium text-foreground">
                      Support Ticket Updates
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Receive updates when there's activity on your support tickets
                    </p>
                  </div>
                  <Switch
                    id="ticket_updates"
                    checked={notificationPrefs.ticket_updates}
                    onCheckedChange={(val) => updateNotificationPref('ticket_updates', val)}
                    data-testid="toggle-ticket-updates"
                  />
                </div>

                {/* Estimate Reminders */}
                <div className="flex items-center justify-between py-3 border-b border-border">
                  <div className="space-y-0.5">
                    <Label htmlFor="estimate_reminders" className="text-base font-medium text-foreground">
                      Estimate Reminders
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Get reminded before your estimates expire
                    </p>
                  </div>
                  <Switch
                    id="estimate_reminders"
                    checked={notificationPrefs.estimate_reminders}
                    onCheckedChange={(val) => updateNotificationPref('estimate_reminders', val)}
                    data-testid="toggle-estimate-reminders"
                  />
                </div>

                {/* Invoice Reminders */}
                <div className="flex items-center justify-between py-3 border-b border-border">
                  <div className="space-y-0.5">
                    <Label htmlFor="invoice_reminders" className="text-base font-medium text-foreground">
                      Invoice Reminders
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Receive reminders about upcoming invoice due dates
                    </p>
                  </div>
                  <Switch
                    id="invoice_reminders"
                    checked={notificationPrefs.invoice_reminders}
                    onCheckedChange={(val) => updateNotificationPref('invoice_reminders', val)}
                    data-testid="toggle-invoice-reminders"
                  />
                </div>

                {/* Backorder Updates */}
                <div className="flex items-center justify-between py-3 border-b border-border">
                  <div className="space-y-0.5">
                    <Label htmlFor="backorder_updates" className="text-base font-medium text-foreground">
                      Backorder Updates
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Get notified when backordered items are ready to ship
                    </p>
                  </div>
                  <Switch
                    id="backorder_updates"
                    checked={notificationPrefs.backorder_updates}
                    onCheckedChange={(val) => updateNotificationPref('backorder_updates', val)}
                    data-testid="toggle-backorder-updates"
                  />
                </div>

                {/* Marketing Emails */}
                <div className="flex items-center justify-between py-3">
                  <div className="space-y-0.5">
                    <Label htmlFor="marketing_emails" className="text-base font-medium text-foreground">
                      Marketing & Promotions
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Receive news about sales, new products, and special offers
                    </p>
                  </div>
                  <Switch
                    id="marketing_emails"
                    checked={notificationPrefs.marketing_emails}
                    onCheckedChange={(val) => updateNotificationPref('marketing_emails', val)}
                    data-testid="toggle-marketing-emails"
                  />
                </div>
              </div>

              <div className="mt-6 p-4 bg-zinc-800/50 rounded-sm">
                <p className="text-xs text-muted-foreground">
                  <strong>Note:</strong> Critical account notifications (password resets, security alerts) 
                  will always be sent regardless of these preferences.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support" data-testid="support-content">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-foreground">Support Tickets</h2>
              <Button
                onClick={() => setShowNewTicketDialog(true)}
                className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
                data-testid="new-ticket-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Ticket
              </Button>
            </div>
            {ticketsLoading ? (
              <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin text-zinc-400 mx-auto" /></div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-muted-foreground">No support tickets yet</p>
                <p className="text-muted-foreground text-sm mt-1">Need help? Open a new ticket above</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tickets.map((t) => {
                  const statusCfg = {
                    open: { label: 'Open', color: 'bg-blue-500/20 text-blue-400' },
                    in_progress: { label: 'In Progress', color: 'bg-yellow-500/20 text-yellow-400' },
                    waiting: { label: 'Waiting', color: 'bg-purple-500/20 text-purple-400' },
                    closed: { label: 'Closed', color: 'bg-zinc-500/20 text-zinc-400' }
                  };
                  const priorityCfg = {
                    low: { label: 'Low', color: 'bg-zinc-500/20 text-zinc-400' },
                    medium: { label: 'Medium', color: 'bg-blue-500/20 text-blue-400' },
                    high: { label: 'High', color: 'bg-orange-500/20 text-orange-400' },
                    urgent: { label: 'Urgent', color: 'bg-red-500/20 text-red-400' }
                  };
                  const s = statusCfg[t.status] || statusCfg.open;
                  const p = priorityCfg[t.priority] || priorityCfg.medium;
                  return (
                    <div
                      key={t.id}
                      className="bg-card border border-border rounded-sm p-4 cursor-pointer hover:border-zinc-600 transition-colors"
                      onClick={() => navigate(`/support/${t.id}`)}
                      data-testid={`ticket-card-${t.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-muted-foreground mb-1">{t.ticket_number}</p>
                          <h3 className="font-medium text-foreground truncate">{t.subject}</h3>
                          {t.description && (
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{t.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge className={p.color}>{p.label}</Badge>
                          <Badge className={s.color}>{s.label}</Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(t.created_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="h-3 w-3" /> View Details
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Change Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="change-email-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Change Email Address</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">
              A verification link will be sent to your new email address. Your email won't change until you click that link.
            </p>
            <div>
              <Label className="text-muted-foreground text-sm">New Email</Label>
              <Input
                type="email"
                value={emailForm.new_email}
                onChange={(e) => setEmailForm(f => ({ ...f, new_email: e.target.value }))}
                placeholder="newemail@example.com"
                className="mt-1 bg-zinc-800 border-zinc-700"
                data-testid="new-email-input"
              />
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">Current Password</Label>
              <Input
                type="password"
                value={emailForm.password}
                onChange={(e) => setEmailForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Enter your password to confirm"
                className="mt-1 bg-zinc-800 border-zinc-700"
                data-testid="confirm-password-input"
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setShowEmailDialog(false)} className="border-zinc-700">Cancel</Button>
              <Button
                onClick={handleChangeEmail}
                disabled={emailChanging}
                className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
                data-testid="confirm-email-change-btn"
              >
                {emailChanging ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Send Verification
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Address Add/Edit Dialog */}
      <Dialog open={showAddressDialog} onOpenChange={setShowAddressDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="address-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">{editingAddress ? 'Edit Address' : 'Add New Address'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-muted-foreground text-sm">Label</Label>
              <Input
                value={addressForm.label}
                onChange={(e) => setAddressForm(f => ({ ...f, label: e.target.value }))}
                placeholder="e.g. Home, Office, Warehouse"
                className="mt-1 bg-zinc-800 border-zinc-700"
                data-testid="address-label-input"
              />
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">Street Address *</Label>
              <Input
                value={addressForm.street}
                onChange={(e) => setAddressForm(f => ({ ...f, street: e.target.value }))}
                placeholder="123 Main Street"
                className="mt-1 bg-zinc-800 border-zinc-700"
                data-testid="address-street-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-muted-foreground text-sm">City *</Label>
                <Input
                  value={addressForm.city}
                  onChange={(e) => setAddressForm(f => ({ ...f, city: e.target.value }))}
                  placeholder="Sydney"
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  data-testid="address-city-input"
                />
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">State *</Label>
                <Input
                  value={addressForm.state}
                  onChange={(e) => setAddressForm(f => ({ ...f, state: e.target.value }))}
                  placeholder="NSW"
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  data-testid="address-state-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-muted-foreground text-sm">Postcode *</Label>
                <Input
                  value={addressForm.postcode}
                  onChange={(e) => setAddressForm(f => ({ ...f, postcode: e.target.value }))}
                  placeholder="2000"
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  data-testid="address-postcode-input"
                />
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">Country</Label>
                <Input
                  value={addressForm.country}
                  onChange={(e) => setAddressForm(f => ({ ...f, country: e.target.value }))}
                  placeholder="Australia"
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  data-testid="address-country-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="address_default"
                checked={addressForm.is_default}
                onCheckedChange={(val) => setAddressForm(f => ({ ...f, is_default: val }))}
                data-testid="address-default-toggle"
              />
              <Label htmlFor="address_default" className="text-muted-foreground text-sm">Set as default address</Label>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setShowAddressDialog(false)} className="border-zinc-700">Cancel</Button>
              <Button
                onClick={handleSaveAddress}
                disabled={addressSaving}
                className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
                data-testid="save-address-btn"
              >
                {addressSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editingAddress ? 'Update Address' : 'Add Address'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Ticket Dialog */}
      <Dialog open={showNewTicketDialog} onOpenChange={setShowNewTicketDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="new-ticket-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">New Support Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-muted-foreground text-sm">Subject *</Label>
              <Input
                value={newTicket.subject}
                onChange={(e) => setNewTicket(f => ({ ...f, subject: e.target.value }))}
                placeholder="What do you need help with?"
                className="mt-1 bg-zinc-800 border-zinc-700"
                data-testid="ticket-subject-input"
              />
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">Description</Label>
              <Textarea
                value={newTicket.description}
                onChange={(e) => setNewTicket(f => ({ ...f, description: e.target.value }))}
                placeholder="Provide as much detail as possible..."
                className="mt-1 bg-zinc-800 border-zinc-700 min-h-[120px]"
                data-testid="ticket-description-input"
              />
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">Priority</Label>
              <Select value={newTicket.priority} onValueChange={(val) => setNewTicket(f => ({ ...f, priority: val }))}>
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700" data-testid="ticket-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setShowNewTicketDialog(false)} className="border-zinc-700">Cancel</Button>
              <Button
                onClick={handleCreateTicket}
                disabled={ticketCreating}
                className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
                data-testid="submit-ticket-btn"
              >
                {ticketCreating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Submit Ticket
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
